import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Check, Shield, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { fetchOpusAiSettings, updateOpusAiSettings } from '@/lib/api';

export function AiSettings() {
  const queryClient = useQueryClient();
  const [apiKey, setApiKey] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [maxTokens, setMaxTokens] = useState('');
  const [costLimit, setCostLimit] = useState('');
  const [saved, setSaved] = useState(false);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['opus-ai-settings'],
    queryFn: fetchOpusAiSettings,
  });

  const saveMutation = useMutation({
    mutationFn: updateOpusAiSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opus-ai-settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      setApiKey('');
    },
  });

  const handleSave = () => {
    const data: Parameters<typeof updateOpusAiSettings>[0] = {};
    if (selectedModel) data.model = selectedModel;
    if (maxTokens) data.maxTokens = parseInt(maxTokens, 10);
    if (apiKey) data.apiKey = apiKey;
    if (costLimit) data.costLimitMonthly = parseFloat(costLimit);
    saveMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-text-tertiary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Current Status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4 text-emerald-400" />
            AI Service Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${settings?.apiKeyConfigured ? 'bg-emerald-500' : 'bg-red-500'}`} />
            <span className="text-sm text-text-primary">
              {settings?.apiKeyConfigured ? 'API Key Configured' : 'API Key Not Configured'}
            </span>
          </div>
          {settings && (
            <div className="mt-3 space-y-1 text-sm text-text-secondary">
              <div>Current Model: <span className="text-text-primary">{settings.model}</span></div>
              <div>Max Tokens: <span className="text-text-primary">{settings.maxTokens.toLocaleString()}</span></div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Model Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Model Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="model">AI Model</Label>
            <Select
              value={selectedModel || settings?.model || ''}
              onValueChange={setSelectedModel}
            >
              <SelectTrigger id="model">
                <SelectValue placeholder="Select model" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="claude-haiku-4-5-20251001">
                  Claude Haiku 4.5 - Fast & affordable ($1/$5 per 1M tokens)
                </SelectItem>
                <SelectItem value="claude-sonnet-4-5-20250929">
                  Claude Sonnet 4.5 - Balanced ($3/$15 per 1M tokens)
                </SelectItem>
                <SelectItem value="claude-opus-4-6">
                  Claude Opus 4.6 - Most capable ($15/$75 per 1M tokens)
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-text-tertiary">
              Choose the model that balances cost and capability for your needs.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="maxTokens">Max Response Tokens</Label>
            <Input
              id="maxTokens"
              type="number"
              min="256"
              max="16384"
              placeholder={String(settings?.maxTokens ?? 4096)}
              value={maxTokens}
              onChange={(e) => setMaxTokens(e.target.value)}
            />
            <p className="text-xs text-text-tertiary">
              Maximum tokens per AI response (256-16,384). Higher values allow longer responses but cost more.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* API Key */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">API Key</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="apiKey">Anthropic API Key</Label>
            <Input
              id="apiKey"
              type="password"
              placeholder={settings?.apiKeyConfigured ? '••••••••••••••••' : 'sk-ant-...'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
            <p className="text-xs text-text-tertiary">
              Enter your Anthropic API key. This will be stored securely as an environment variable.
            </p>
          </div>

          {!settings?.apiKeyConfigured && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-900/20 border border-amber-500/30">
              <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-200">
                AI features are disabled. Configure an API key to enable the Opus AI assistant.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cost Limits */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Cost Controls</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="costLimit">Monthly Cost Limit (USD)</Label>
            <Input
              id="costLimit"
              type="number"
              min="0"
              step="0.01"
              placeholder="No limit"
              value={costLimit}
              onChange={(e) => setCostLimit(e.target.value)}
            />
            <p className="text-xs text-text-tertiary">
              Set a monthly spending cap for AI usage. Leave empty for no limit.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : saved ? (
            <>
              <Check className="mr-2 h-4 w-4" />
              Saved
            </>
          ) : (
            'Save Settings'
          )}
        </Button>
      </div>
    </div>
  );
}
